import { useCallback, useEffect, useState } from 'react';
import Intercom, {
    boot as intercomBoot,
    shutdown,
    show,
    update,
    hide,
} from '@intercom/messenger-js-sdk';
import useAuth from './useAuth';
import { useQuery } from "@apollo/client/react";
import { useSnackbar } from 'notistack';
import { IntercomAuth } from '../@types/shared.types';
import { GET_INTERCOM_AUTH } from '../graphql/resolvers/queries';
import { IntercomSettings } from '@intercom/messenger-js-sdk/dist/types';
import { env } from '../config/env';

export default function useIntercomMessenger() {
    const { user } = useAuth();
    const { enqueueSnackbar } = useSnackbar();

    // Track Intercom state
    const [isInitialized, setIsInitialized] = useState(false);
    const [hasBeenShutdown, setHasBeenShutdown] = useState(false);
    const [jwtPayload, setJwtPayload] = useState<string | null>(null);

    // Call to sign raw payload
    const { data: intercomAuthData, error: intercomAuthError } = useQuery(GET_INTERCOM_AUTH, {
        skip: !user,
    });

    useEffect(() => {
        if (intercomAuthData) {
            const auth: IntercomAuth = intercomAuthData.getIntercomAuth as IntercomAuth;
            setJwtPayload(auth.token);
        }
    }, [intercomAuthData]);

    useEffect(() => {
        if (intercomAuthError) {
            enqueueSnackbar(`Error signing Intercom payload: ${intercomAuthError.message}`, {
                variant: 'error',
            });
        }
    }, [intercomAuthError]);

    // Effect to handle user changes
    useEffect(() => {
        if (!user) {
            shutdown();
            setIsInitialized(false);
            setHasBeenShutdown(true);
            setJwtPayload(null);
            return;
        }
        
    }, [user]);

    // Custom boot function that handles initialization vs re-boot
    const bootWithUserData = useCallback(() => {
        if (!jwtPayload) return;

        console.log('Booting Intercom with JWT:', jwtPayload);

        const intercomConfig:IntercomSettings = {
            app_id: env.intercom.appId,
            intercom_user_jwt: jwtPayload, // JWT token for secure mode
        };

        if (!isInitialized || hasBeenShutdown) {
            // First time initialization or after shutdown
            Intercom(intercomConfig);
            setIsInitialized(true);
            setHasBeenShutdown(false);
        } else {
            // Use the boot method for re-initialization
            intercomBoot(intercomConfig);
        }
        hide(); 
    }, [jwtPayload, isInitialized, hasBeenShutdown]);

    // Custom shutdown function to track state
    const shutdownIntercom = useCallback(() => {
        shutdown();
        setIsInitialized(false);
        setHasBeenShutdown(true);
    }, []);

    const updateUserData = useCallback(() => {
        if (!jwtPayload || !isInitialized) return;

        update({
            intercom_user_jwt: jwtPayload,
        });
    }, [jwtPayload, isInitialized]);

    // Auto-boot when JWT is ready
    useEffect(() => {
        if (jwtPayload && user) {
            bootWithUserData();
        }
    }, [jwtPayload, user, bootWithUserData]);

    return {
        show,
        hide,
        boot: bootWithUserData,
        shutdown: shutdownIntercom,
        updateUserData,
        isReady: !!jwtPayload && isInitialized,
    };
}
